import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Zap } from 'lucide-react';

import { perfColor, perfBar } from '@/utils/styleHelpers';

export const KPIPlatformTab = ({ automatedKpis, getTrendIcon }) => (
  <div className="space-y-6">
    {automatedKpis.length > 0 ? (
      <>
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold flex items-center gap-2" data-testid="platform-kpis-heading">
              <Zap className="h-5 w-5 text-cyan-500" /> Live Platform Metrics
            </h2>
            <p className="text-sm text-muted-foreground">Auto-computed from real data imported into the platform</p>
          </div>
          <Badge variant="outline" className="text-xs">{automatedKpis.length} metrics</Badge>
        </div>
        {Object.entries(
          automatedKpis.reduce((acc, kpi) => {
            const cat = kpi.category;
            if (!acc[cat]) acc[cat] = [];
            acc[cat].push(kpi);
            return acc;
          }, {})
        ).map(([category, catKpis]) => (
          <Card key={category}>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">{category}</CardTitle>
              <CardDescription>{catKpis.length} metric{catKpis.length !== 1 ? 's' : ''} from {catKpis[0]?.source}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {catKpis.map(kpi => {
                  const performance = kpi.target > 0 ? Math.round((kpi.value / kpi.target) * 100) : 100;
                  const textCls = perfColor(performance);
                  const barCls = perfBar(performance);
                  return (
                    <div key={kpi.metric_id} className="p-4 border border-muted rounded-lg" data-testid={`auto-kpi-${kpi.metric_id}`}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium truncate">{kpi.name}</span>
                        {getTrendIcon(kpi.trend)}
                      </div>
                      <p className={`text-2xl font-bold ${textCls}`}>
                        {kpi.value}{kpi.unit === '%' ? '%' : ''} <span className="text-sm font-normal text-muted-foreground">{kpi.unit !== '%' ? kpi.unit : ''}</span>
                      </p>
                      {kpi.target > 0 && kpi.unit === '%' && (
                        <div className="mt-2">
                          <div className="flex justify-between text-xs text-muted-foreground mb-1">
                            <span>Target: {kpi.target}{kpi.unit === '%' ? '%' : ''}</span>
                            <span className={textCls}>{performance}%</span>
                          </div>
                          <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                            <div className={`h-full rounded-full ${barCls}`} style={{ width: `${Math.min(performance, 100)}%` }} />
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        ))}
      </>
    ) : (
      <Card className="py-16">
        <CardContent className="text-center">
          <Zap className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
          <h3 className="text-lg font-semibold mb-2">No Platform Data Yet</h3>
          <p className="text-muted-foreground text-sm mb-4">Import data into Order Picking, Expedition, or other modules to see automated KPIs here.</p>
        </CardContent>
      </Card>
    )}
  </div>
);
